import { AnimatePresence, motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { ExternalLink, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import {
  BrandLockup,
  IconButton,
  LogoMark,
  Tooltip,
  cn,
  useIsDesktop,
} from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";
import { HELPER_LINKS, NAV_SECTIONS } from "./navigation";
import { EnvironmentBadge } from "./EnvironmentBadge";

const EXPANDED_WIDTH = 288; // w-72
const COLLAPSED_WIDTH = 76;

/**
 * Navigation rail.
 *
 * Three states, not two: an overlay drawer below `lg`, and on desktop either a
 * full rail or a collapsed icon rail. Collapsing is a desktop-only idea — on a
 * phone the rail is already hidden, so the toggle would mean nothing there.
 *
 * Items the signed-in user has no permission for are removed rather than
 * disabled, so the menu is an honest map of what they can actually do.
 */
export function Sidebar({
  open,
  onClose,
  collapsed,
  onToggleCollapsed,
}: {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const isDesktop = useIsDesktop();

  // Collapsing only applies to the desktop rail; the drawer is always full width.
  const isCollapsed = isDesktop && collapsed;

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-ink-950/60 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      {/*
        Framer writes an inline transform and width, which `lg:` classes cannot
        beat. Drive both from the breakpoint instead so the rail is simply always
        open on desktop, at whichever width the collapse preference asks for.
      */}
      <motion.aside
        initial={false}
        animate={{
          x: isDesktop || open ? 0 : "-100%",
          width: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        }}
        transition={{ type: "spring", stiffness: 420, damping: 38 }}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col border-r border-line bg-surface",
          "lg:sticky lg:top-0 lg:h-dvh",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center gap-2 border-b border-line",
            isCollapsed ? "justify-center px-2" : "justify-between px-4",
          )}
        >
          {isCollapsed ? (
            <Tooltip label="Expand navigation">
              <button
                type="button"
                onClick={onToggleCollapsed}
                aria-label="Expand navigation"
                aria-expanded={false}
                className="rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
              >
                <LogoMark />
              </button>
            </Tooltip>
          ) : (
            <>
              <BrandLockup subtitle="Marketplace admin" />
              <IconButton
                icon={X}
                label="Close navigation"
                size="sm"
                onClick={onClose}
                className="lg:hidden"
              />
              <IconButton
                icon={PanelLeftClose}
                label="Collapse navigation"
                size="sm"
                onClick={onToggleCollapsed}
                className="hidden lg:inline-flex"
              />
            </>
          )}
        </div>

        <nav
          aria-label="Main"
          className={cn(
            "mask-fade-b min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-4",
            isCollapsed ? "space-y-3 px-2" : "space-y-6 px-3",
          )}
        >
          {NAV_SECTIONS.map((section) => (
            <NavSectionBlock
              key={section.id}
              section={section}
              collapsed={isCollapsed}
              onNavigate={onClose}
            />
          ))}

          <div>
            <SectionHeading collapsed={isCollapsed}>Broker portals</SectionHeading>
            <ul className="space-y-0.5">
              {HELPER_LINKS.map((link) => (
                <li key={link.href}>
                  <Tooltip label={link.label} enabled={isCollapsed} className="w-full">
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "group flex w-full items-center rounded-lg py-2 text-sm text-content-muted",
                        "transition-colors hover:bg-surface-sunken hover:text-content",
                        isCollapsed ? "justify-center px-0" : "gap-3 px-3",
                      )}
                    >
                      <ExternalLink
                        size={16}
                        aria-hidden="true"
                        className="shrink-0 opacity-70"
                      />
                      {!isCollapsed && (
                        <span className="flex-1 truncate">{link.label}</span>
                      )}
                      {isCollapsed && <span className="sr-only">{link.label}</span>}
                    </a>
                  </Tooltip>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div
          className={cn(
            "shrink-0 border-t border-line py-3",
            isCollapsed ? "px-2" : "px-4",
          )}
        >
          {isCollapsed ? (
            <div className="flex justify-center">
              <Tooltip label="Expand navigation">
                <IconButton
                  icon={PanelLeftOpen}
                  label="Expand navigation"
                  size="sm"
                  onClick={onToggleCollapsed}
                />
              </Tooltip>
            </div>
          ) : (
            <EnvironmentBadge />
          )}
        </div>
      </motion.aside>
    </>
  );
}

function SectionHeading({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: React.ReactNode;
}) {
  if (collapsed) {
    // A rule stands in for the heading: it keeps the visual grouping without
    // squeezing a word into 76px or dropping the structure entirely.
    return <div aria-hidden="true" className="mx-2 mb-2 h-px bg-line" />;
  }

  return (
    <h2 className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-content-subtle">
      {children}
    </h2>
  );
}

function NavSectionBlock({
  section,
  collapsed,
  onNavigate,
}: {
  section: (typeof NAV_SECTIONS)[number];
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { can } = useAuth();

  const visible = section.items.filter(
    (item) => !item.permission || can(item.permission),
  );
  if (visible.length === 0) return null;

  return (
    <div>
      <SectionHeading collapsed={collapsed}>{section.label}</SectionHeading>
      <ul className="space-y-0.5">
        {visible.map((item) => (
          <li key={item.to}>
            <Tooltip label={item.label} enabled={collapsed} className="w-full">
              <NavLink
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "group relative flex w-full items-center rounded-lg py-2 text-sm transition-colors",
                    collapsed ? "justify-center px-0" : "gap-3 px-3",
                    isActive
                      ? "bg-gold-100 font-medium text-gold-900 dark:bg-gold-950/60 dark:text-gold-200"
                      : "text-content-muted hover:bg-surface-sunken hover:text-content",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-rail"
                        aria-hidden="true"
                        className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-gold-sheen"
                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                      />
                    )}
                    <item.icon
                      size={16}
                      aria-hidden="true"
                      className={cn(
                        "shrink-0 transition-colors",
                        isActive ? "text-gold-600 dark:text-gold-400" : "opacity-70",
                      )}
                    />
                    {collapsed ? (
                      <span className="sr-only">{item.label}</span>
                    ) : (
                      <span className="flex-1 truncate">{item.label}</span>
                    )}
                  </>
                )}
              </NavLink>
            </Tooltip>
          </li>
        ))}
      </ul>
    </div>
  );
}
